import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axiosClient from '../api/axiosClient';

export const AuthContext = createContext();

const PROGRAM_COORDINATOR_ROLE = 'Program Coordinator';

const fetchCoordinatorPermissions = async (userId) => {
  try {
    const response = await axiosClient.get(`users/program-coordinator/${userId}/permissions`);
    if (response.data?.permissions) {
      return response.data.permissions.reduce((acc, p) => {
        acc[p.key] = p.enabled;
        return acc;
      }, {});
    }
  } catch {
    // permissions stay empty on error
  }
  return {};
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('user') !== null && localStorage.getItem('accessToken') !== null;
  });

  const [authenticatedUser, setAuthenticatedUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });

  const [role, setRole] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser).role?.role : null;
    } catch {
      return null;
    }
  });

  const [permissions, setPermissions] = useState({});

  // Fetch permissions on mount if already authenticated as Program Coordinator
  useEffect(() => {
    const storedUser = (() => {
      try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
    })();
    if (storedUser?.role?.role === PROGRAM_COORDINATOR_ROLE && storedUser?.id) {
      fetchCoordinatorPermissions(storedUser.id).then(setPermissions);
    }
  }, []);

  const doLogin = async ({ user, accessToken, refreshToken }) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setIsAuthenticated(true);
    setAuthenticatedUser(user);
    setRole(user?.role?.role);

    if (user?.role?.role === PROGRAM_COORDINATOR_ROLE && user?.id) {
      const perms = await fetchCoordinatorPermissions(user.id);
      setPermissions(perms);
    }
  };

  const doLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setIsAuthenticated(false);
    setAuthenticatedUser(null);
    setRole(null);
    setPermissions({});
  };

  const setUser = (user) => {
    localStorage.setItem('user', JSON.stringify(user));
    setAuthenticatedUser(user);
  };

  const can = useCallback((key) => permissions[key] ?? false, [permissions]);

  useEffect(() => {
    let location = '';
    const handleStorageChange = (event) => {
      if (event.key === 'user') {
        if (!event.newValue) {
          location = window.location.href;
          console.log(location);
          doLogout();
        } else {
          window.location.assign(location);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      authenticatedUser,
      role,
      permissions,
      can,
      setUser,
      doLogin,
      doLogout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
